
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";

type PasswordFieldProps = {
  id: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  minLength?: number;
  error?: string;
  showStrengthIndicator?: boolean;
};

const PasswordField = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  required = false,
  minLength,
  error,
  showStrengthIndicator = false
}: PasswordFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: "", color: "bg-gray-200" };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    score = Object.values(checks).filter(Boolean).length;
    
    if (score <= 2) return { score, label: "Weak", color: "bg-red-500" };
    if (score <= 3) return { score, label: "Fair", color: "bg-yellow-500" };
    if (score <= 4) return { score, label: "Good", color: "bg-blue-500" };
    return { score, label: "Strong", color: "bg-green-500" };
  };

  const strength = getPasswordStrength(value);

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          name={id}
          type={showPassword ? "text" : "password"}
          placeholder={placeholder}
          className={`pr-10 ${error ? "border-destructive" : ""}`}
          value={value}
          onChange={onChange}
          required={required}
          minLength={minLength}
          aria-invalid={!!error}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
          onClick={togglePasswordVisibility}
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Eye className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </div>
      
      {showStrengthIndicator && value && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${strength.color}`}
                style={{ width: `${(strength.score / 5) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              {strength.label}
            </span>
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>Password requirements:</p>
            <ul className="list-none space-y-1">
              <li className={`flex items-center ${value.length >= 8 ? 'text-green-600' : ''}`}>
                <span className="mr-2">{value.length >= 8 ? '✓' : '○'}</span>
                At least 8 characters
              </li>
              <li className={`flex items-center ${/[a-z]/.test(value) ? 'text-green-600' : ''}`}>
                <span className="mr-2">{/[a-z]/.test(value) ? '✓' : '○'}</span>
                One lowercase letter
              </li>
              <li className={`flex items-center ${/[A-Z]/.test(value) ? 'text-green-600' : ''}`}>
                <span className="mr-2">{/[A-Z]/.test(value) ? '✓' : '○'}</span>
                One uppercase letter
              </li>
              <li className={`flex items-center ${/\d/.test(value) ? 'text-green-600' : ''}`}>
                <span className="mr-2">{/\d/.test(value) ? '✓' : '○'}</span>
                One number
              </li>
            </ul>
          </div>
        </div>
      )}
      
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};

export default PasswordField;
